import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Alert, Carousel } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { chartApi, hotIssueApi } from '../services/api';
import { Chart, HotIssue } from '../types';
import ChartCard from '../components/ChartCard';
import HotIssueModal from '../components/HotIssueModal';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [recentCharts, setRecentCharts] = useState<Chart[]>([]);
  const [hotIssues, setHotIssues] = useState<HotIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedHotIssue, setSelectedHotIssue] = useState<HotIssue | null>(null);
  const [showHotIssueModal, setShowHotIssueModal] = useState(false);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [chartsData, issuesData, categoriesData] = await Promise.all([
          chartApi.getRecentCharts(),
          hotIssueApi.getHotIssues(),
          chartApi.getAllCategories()
        ]);
        setRecentCharts(chartsData);
        setHotIssues(issuesData);
        setCategories(categoriesData);
      } catch (err) {
        setError('데이터를 불러오는데 실패했습니다.');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 검색 필터링
  const filteredCharts = recentCharts.filter(chart =>
    chart.title.toLowerCase().includes(search.toLowerCase()) ||
    (Array.isArray(chart.keywords) && chart.keywords.some(k => k && k.toLowerCase().includes(search.toLowerCase())))
  );

  const handleHotIssueClick = (hotIssue: HotIssue) => {
    setSelectedHotIssue(hotIssue);
    setShowHotIssueModal(true);
  };

  const handleCategoryClick = (categoryName: string) => {
    navigate(`/category/${categoryName}`);
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <div className="d-flex flex-column align-items-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="text-muted">차트 데이터를 불러오는 중...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="theme-card">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <Container className="py-5">
        {/* 검색창 */}
        {/* <Row className="mb-4">
          <Col md={6} className="mx-auto">
            <input
              type="text"
              className="form-control form-control-lg shadow-sm"
              placeholder="차트 제목, 키워드로 검색..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ borderRadius: '999px', fontSize: '1.1rem', padding: '1em 1.5em' }}
            />
          </Col>
        </Row> */}
        {/* 히어로 섹션 */}
        <Row className="mb-5 hero-section-mobile">
          <Col className="text-center">
            <h1 className="display-4 fw-bold mb-3 gradient-text">
              <span className="d-none d-md-inline">세계의 모든 순위</span>
              <span className="d-md-none" style={{ fontSize: '2.5rem' }}>세계의 모든 순위</span>
            </h1>
            <p className="lead text-muted mb-4 hero-description-mobile">
              <span className="d-none d-md-inline">
                경제, 자연, 스포츠, 엔터테인먼트까지<br />
                다양한 분야의 흥미로운 순위를 한눈에 확인하세요
              </span>
              <span className="d-md-none" style={{ fontSize: '1rem' }}>
                경제, 자연, 스포츠, 엔터테인먼트까지<br />
                다양한 분야의 흥미로운 순위를 한눈에 확인하세요
              </span>
            </p>
          </Col>
        </Row>

        {/* 핫이슈 섹션 (위치 변경) */}
        <Row className="mb-5">
          <Col>
            <div className="d-flex align-items-center mb-4">
              <div className="gradient-bg-secondary rounded-circle d-flex align-items-center justify-content-center me-3" 
                   style={{ width: '50px', height: '50px' }}>
                <i className="fas fa-fire text-white fs-5"></i>
              </div>
              <h2 className="mb-0 fw-bold gradient-text" style={{ color: '#23272b', background: 'none', WebkitBackgroundClip: 'unset', WebkitTextFillColor: 'unset' }}>이번주 핫이슈</h2>
            </div>
            <Carousel interval={5000} className="shadow-lg rounded-3 overflow-hidden">
              {hotIssues.map((issue, index) => (
                <Carousel.Item key={issue.id}>
                  <Card 
                    className="border-0 cursor-pointer" 
                    style={{ 
                      background: 'var(--gradient-primary)',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleHotIssueClick(issue)}
                  >
                    <Card.Body className="text-center py-5 text-white">
                      <div className="mb-4">
                        <i className="fas fa-fire fs-1 mb-3 d-block"></i>
                        <h3 className="mb-3 fw-bold">{issue.title}</h3>
                        <p className="lead mb-0 opacity-90">{issue.description}</p>
                      </div>
                      <div className="d-flex justify-content-center align-items-center gap-3">
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '0.35em 0.65em',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            lineHeight: 1,
                            color: 'var(--text-secondary)',
                            backgroundColor: 'var(--bg-secondary)',
                            borderRadius: '999px',
                            marginRight: '0.5em',
                            marginBottom: '0.25em'
                          }}
                        >
                          <i className="fas fa-star me-1"></i>
                          핫이슈
                        </span>
                        <small className="opacity-75">
                          <i className="fas fa-calendar me-1"></i>
                          {new Date(issue.created_at).toLocaleDateString('ko-KR')}
                        </small>
                      </div>
                    </Card.Body>
                  </Card>
                </Carousel.Item>
              ))}
            </Carousel>
          </Col>
        </Row>

        {/* 최근 차트 섹션 (위치 변경) */}
        <Row className="mb-5">
          <Col>
            <div className="d-flex align-items-center mb-4">
              <div className="gradient-bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" 
                   style={{ width: '50px', height: '50px' }}>
                <i className="fas fa-clock text-white fs-5"></i>
              </div>
              <h2 className="mb-0 fw-bold gradient-text" style={{ color: '#23272b', background: 'none', WebkitBackgroundClip: 'unset', WebkitTextFillColor: 'unset' }}>최근 업데이트된 차트</h2>
            </div>
            <Row>
              {filteredCharts.length === 0 ? (
                <Col className="text-center py-5">
                  <i className="fas fa-search fa-2x text-muted mb-3 d-block"></i>
                  <h5 className="text-muted">검색 결과가 없습니다.</h5>
                </Col>
              ) : (
                filteredCharts.map((chart) => (
                  <Col key={chart.id} lg={4} md={6} className="mb-4">
                    <ChartCard chart={chart} categories={categories} />
                  </Col>
                ))
              )}
            </Row>
          </Col>
        </Row>

        {/* 카테고리별 빠른 접근 */}
        <Row>
          <Col>
            <div className="d-flex align-items-center mb-4">
              <div className="gradient-bg-success rounded-circle d-flex align-items-center justify-content-center me-3" 
                   style={{ width: '50px', height: '50px' }}>
                <i className="fas fa-th-large text-white fs-5"></i>
              </div>
              <h2 className="mb-0 fw-bold" style={{ color: '#23272b' }}>카테고리별 탐색</h2>
            </div>
            <Row>
              {categories.map((category: any, idx: number) => (
                <Col key={category.id} lg={2} md={3} sm={4} xs={6} className="mb-4">
                  <Card 
                    className="theme-card text-center h-100 border-0 cursor-pointer"
                    style={{ background: 'var(--card-bg)', color: 'var(--text-primary)' }}
                    onClick={() => handleCategoryClick(category.name)}
                  >
                    <Card.Body className="py-4">
                      <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                           style={{ 
                             width: '60px', 
                             height: '60px',
                             backgroundColor: `${category.color}20`,
                             fontSize: '2rem'
                           }}>
                        <span>{category.icon}</span>
                      </div>
                      <h6 className="mb-2 fw-bold category-title">{category.name}</h6>
                      <small className="category-desc">{category.description}</small>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      </Container>

      {/* 핫이슈 모달 */}
      <HotIssueModal
        hotIssue={selectedHotIssue}
        show={showHotIssueModal}
        onHide={() => {
          setShowHotIssueModal(false);
          setSelectedHotIssue(null);
        }}
      />
    </div>
  );
};

export default Home; 